import { motion } from "framer-motion";
import { Fingerprint, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOpenPatterns } from "@/contexts/ModalContext";

// ── Six behavioural patterns ──────────────────────────────────
// Names match the scoring model (not guest-facing archetypes).
// Recommended actions are the exact strings the API returns.

const patterns = [
{
  name: "Recovering",
  description: "Poor historical record, strong recent arc. Has demonstrated willingness to change.",
  action: "reminder_only",
  actionLabel: "Reminder only",
  tone: "green"
},
{
  name: "Isolated",
  description: "Single negative event against otherwise clean history. Treat as exception, not pattern.",
  action: "reminder_only",
  actionLabel: "Reminder only",
  tone: "green"
},
{
  name: "Disorganised",
  description: "Multiple late cancellations. No no-shows. Likely forgets or overcommits.",
  action: "confirmation_required",
  actionLabel: "Confirmation required",
  tone: "amber"
},
{
  name: "Optimistic",
  description: "No-shows with no prior late-cancel history. Books in hope, disappears without warning.",
  action: "deposit_suggested",
  actionLabel: "Deposit suggested",
  tone: "amber"
},
{
  name: "Declining",
  description: "Score trending downward over recent bookings. Pattern worsening, not improving.",
  action: "deposit_required",
  actionLabel: "Deposit required",
  tone: "red"
},
{
  name: "Combined",
  description: "Both no-shows and late cancellations. Systemic pattern across multiple event types.",
  action: "deposit_required",
  actionLabel: "Mandatory deposit",
  tone: "red"
}];


const toneClasses = {
  green: {
    border: "border-emerald-500/20",
    bg: "bg-emerald-500/5",
    dot: "bg-emerald-400",
    label: "text-emerald-400",
    labelBg: "bg-emerald-400/10"
  },
  amber: {
    border: "border-amber-500/20",
    bg: "bg-amber-500/5",
    dot: "bg-amber-400",
    label: "text-amber-400",
    labelBg: "bg-amber-400/10"
  },
  red: {
    border: "border-red-500/20",
    bg: "bg-red-500/5",
    dot: "bg-red-400",
    label: "text-red-400",
    labelBg: "bg-red-400/10"
  }
};

// ── Component ─────────────────────────────────────────────────

const PhilosophySection = () => {
  const { setOpen: setPatternsOpen } = useOpenPatterns();
  return (
    <section
      id="philosophy"
      className="relative py-12 md:py-24 overflow-x-hidden max-w-full">

      {/* Subtle background grid */}
      <div className="absolute inset-0 bg-grid opacity-[0.08]" />

      <div className="container relative mx-auto px-6">
        {/* Section label */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-4 flex justify-center">

          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            <Fingerprint className="h-4 w-4" />
            Behaviour Design
          </span>
        </motion.div>

        {/* ── Two-column layout ── */}
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">

          {/* Left: argument — 3/5 width */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}>

            <h2 className="text-4xl font-bold text-foreground leading-tight mb-6 md:text-5xl break-words max-w-full">
              Not a longer list.
              <br />
              <span className="text-primary">A reason to show up.</span>
            </h2>

            <div className="space-y-4 text-muted-foreground text-lg leading-relaxed break-words max-w-full">
              <p>
                Deposits, cancellation fees, and internal lists catch failure
                after the cost is paid. They don't change the probability of
                failure.
              </p>
              <p>
                ClearSlot scores the guest, not the booking. Right friction,
                right guest, at the point of intent.
              </p>
              <p>
                A guest's behaviour on five other platforms is part of who they
                are on yours.
              </p>
            </div>

            {/* Score callout blocks */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border bg-card/50 px-5 py-4">
                <p className="text-3xl font-bold text-foreground mb-1.5">75</p>
                <p className="text-sm font-semibold text-foreground mb-1">
                  Starting score
                </p>
                <p className="text-sm text-muted-foreground">
                  New guests start at 75, not 100. Reputation is built from
                  observed behaviour, not assumed on arrival.
                </p>
              </div>

              <div className="rounded-xl border border-border bg-card/50 px-5 py-4">
                <p className="text-3xl font-bold text-foreground mb-1.5">↑</p>
                <p className="text-sm font-semibold text-foreground mb-1">
                  Recovery is built in
                </p>
                <p className="text-sm text-muted-foreground">
                  Completed bookings move the score up. One no-show plus twelve
                  completions ≠ same risk.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right: six pattern cards — 2/5 width */}
          <div className="lg:col-span-2">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4">

              Behavioural patterns → recommended actions
            </motion.p>

            <div className="flex flex-col md:flex-col overflow-visible pb-0 max-w-full gap-0">
              {patterns.map((p, i) => {
                const t = toneClasses[p.tone as keyof typeof toneClasses];
                return (
                  <motion.div
                    key={p.name}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.06 }}
                    className={`rounded-lg border ${t.border} ${t.bg} px-4 py-3 flex items-start gap-3 w-full h-auto min-w-0 overflow-hidden`}>

                    {/* Tone dot */}
                    <span
                      className={`mt-1 h-2 w-2 rounded-full shrink-0 ${t.dot}`} />


                    <div className="min-w-0 flex-1 flex flex-col">
                      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-bold text-foreground">
                          {p.name}
                        </p>
                        <span
                          className={`shrink-0 text-[10px] font-mono font-medium px-1.5 py-0.5 rounded ${t.labelBg} ${t.label}`}>

                          {p.actionLabel}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4 md:line-clamp-none">
                        {p.description}
                      </p>
                    </div>
                  </motion.div>);

              })}
            </div>



            {/* Engine note */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="mt-4 font-mono text-xs text-muted-foreground">

              Deterministic. No ML. No heuristics.
              <br />
              Signals expire automatically via TTL.
            </motion.p>
          </div>
        </div>

        {/* Formula CTA */}
        <div className="mt-12 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            className="text-sm min-h-[44px] md:min-h-0"
            onClick={() => setPatternsOpen(true)}
          >
            See the formula in action →
          </Button>
        </div>
      </div>
    </section>);

};

export default PhilosophySection;